import React, { useEffect, useState } from "react";
import { Customer, getCustomer } from "@/api/customers";
import SearchBox from "@/ui-controls/SearchBox";
import { IconButton, Shimmer, ShimmerElementType } from "@fluentui/react";
import {
  AppRegistration,
  getAppRegistrationsByCustomerId,
} from "@/api/appRegistrations";
import AddButton from "@/ui-controls/AddButton";
import NewAppRegistrationPanel from "@/components/customers/customer_app_registrations/NewCustomerAppRegistrationPanel";
import CustomerAppRegistrationsDetailList from "@/components/customers/customer_app_registrations/CustomerAppRegistrationsDetailList";
import { Application, getOpenApiApplications } from "@/api/applications";
import { useNavigate } from "react-router-dom";

interface Props {
  CustomerId: number;
  onCustomerLoaded?: (name: string) => void;
  breadcrumbTitle?: string;
  breadcrumbSubtitle?: React.ReactNode;
}

const CustomerAppRegistrations = (props: Props) => {
  const navigate = useNavigate();
  const [loadingCustomerAppRegistrations, setLoadingCustomerAppRegistrations] =
    useState<boolean>(false);
  const [customer, setCustomer] = useState<Customer | undefined>(undefined);
  const [customerAppRegistrations, setCustomerAppRegistrations] = useState<
    AppRegistration[]
  >([]);
  const [selectedAppRegistration, setSelectedAppRegistration] = useState<
    AppRegistration | undefined
  >(undefined);
  const [filteredAppRegistrations, setFilteredAppRegistrations] = useState<
    AppRegistration[]
  >([]);
  const [isPanelOpen, setIPanelOpen] = useState<boolean>(false);
  const [applications, setApplications] = useState<Application[]>([]);

  useEffect(() => {
    getOpenApiApps();
  }, []);

  useEffect(() => {
    if (props.CustomerId) {
      getCustomerById();
      getCustomerAppRegistrations();
    }
  }, [props.CustomerId]);

  const getCustomerAppRegistrations = () => {
    setLoadingCustomerAppRegistrations(true);
    getAppRegistrationsByCustomerId(props.CustomerId)
      .then((result) => {
        setCustomerAppRegistrations(result);
        setFilteredAppRegistrations(result);
        setLoadingCustomerAppRegistrations(false);
      })
      .catch((error) => {
        handleError(error);
        setLoadingCustomerAppRegistrations(false);
      });
  };

  const getCustomerById = () => {
    getCustomer(props.CustomerId)
      .then((result) => {
        setCustomer(result);
        if (props.onCustomerLoaded && result?.Name) {
          props.onCustomerLoaded(result.Name);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  };

  const handleError = (error: any) => {
    // Error handling would go here in real app
  };

  const handleUserSearch = (searchValue: string) => {
    const filteredAppRegistrations = customerAppRegistrations.filter(
      (appReg) =>
        appReg.AppRegistrationName.toLowerCase().includes(
          searchValue.toLowerCase()
        ) ||
        appReg.ClientName.toLowerCase().includes(searchValue.toLowerCase()) ||
        appReg.ClientDescription.toLowerCase().includes(
          searchValue.toLowerCase()
        ) ||
        appReg.ContactEmail.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredAppRegistrations(filteredAppRegistrations);
  };

  const openPanel = () => {
    setIPanelOpen(true);
  };

  const getOpenApiApps = () => {
    getOpenApiApplications()
      .then((result) => {
        setApplications(result);
      })
      .catch((error) => {
        handleError(error);
      });
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-left-section">
          <IconButton
            iconProps={{ iconName: "Back" }}
            title="Back to Customers"
            onClick={() => navigate("/customers")}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: { fontSize: 18, color: "#0078d4" },
              rootPressed: { backgroundColor: "#e1dfdd" },
            }}
          />
          <div>
            {props.breadcrumbTitle && (
              <h2 className="page-breadcrumb-title">
                {props.breadcrumbTitle}
              </h2>
            )}
            {props.breadcrumbSubtitle && (
              <div style={{ color: "#605e5c", fontSize: "14px" }}>
                {props.breadcrumbSubtitle}
              </div>
            )}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <AddButton
            text="OApi integration"
            onClick={() => {
              setSelectedAppRegistration(undefined);
              openPanel();
            }}
          />
          <SearchBox placeholder="Search" onSearch={handleUserSearch} />
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            ariaLabel="Refresh"
            onClick={() => {
              getCustomerAppRegistrations();
            }}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: { fontSize: 18, color: "#0078d4" },
              rootPressed: { backgroundColor: "#e1dfdd" },
            }}
          />
        </div>
      </div>

      <div>
        <CustomerAppRegistrationsDetailList
          AppRegList={filteredAppRegistrations}
          CustomerId={props.CustomerId}
          onRemoveAppRegistration={() => {
            getCustomerAppRegistrations();
          }}
          onEditAppRegistration={(appReg: AppRegistration) => {
            setIPanelOpen(true);
            setSelectedAppRegistration(appReg);
          }}
          onRefresh={() => {
            getCustomerAppRegistrations();
          }}
        />
      </div>
      <NewAppRegistrationPanel
        appReg={selectedAppRegistration}
        customer={customer}
        applications={applications}
        isPanelOpenned={isPanelOpen}
        onPanelClosed={() => {
          setIPanelOpen(false);
        }}
        onSave={(newAppReg: AppRegistration) => {
          getCustomerAppRegistrations();
        }}
        onUpdate={() => {
          getCustomerAppRegistrations();
        }}
      />
    </>
  );
};

export default CustomerAppRegistrations;
